"use client";

import { MODALS } from "@idss/libraries";
import { handleError, handleSuccess } from "@idss/notification";
import {
  Button,
  CopyButton,
  Flex,
  PinInput,
  Stack,
  Tabs,
  Text,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { Form, useForm, yupResolver } from "@mantine/form";
import { modals } from "@mantine/modals";
import { useMutation } from "@tanstack/react-query";
import { Mail01Icon } from "hugeicons-react";
import { useQueryState } from "nuqs";
import { object, string } from "yup";

import { builder } from "@/builders";
import { QRCodeData } from "@/builders/types/two-fa";
import { QRIcon } from "@/svgs/qr-icon";

import QRCode from "react-qr-code";

const schema = object({
  otp: string().length(6, "OTP must be 6 characters").required(),
});

interface TwoFactorAuthenticationProps extends QRCodeData {
  email: string;
}

export function TwoFactorAuthentication({
  otp_auth_url,
  otp_base32,
  token,
  email,
}: TwoFactorAuthenticationProps) {
  const [activity, setActivity] = useQueryState("2fa", {
    defaultValue: "qr-code",
  });

  const form = useForm({
    initialValues: {
      token,
      email,
      otp: "",
      showToken: false,
    },
    validate: yupResolver(schema),
    validateInputOnBlur: true,
  });

  const { mutate } = useMutation({
    mutationFn: builder.$use.two_fa.verify,
    onError: handleError(),
    onSuccess: ({ message }) => {
      handleSuccess({
        message,
      });
    },
  });

  function handleSubmit({ otp, email, token }: typeof form.values) {
    mutate({
      token,
      email,
      otp,
    });
  }

  function handleShowToken() {
    form.setFieldValue("showToken", !form.values.showToken);
  }

  return (
    <Stack gap={20}>
      <Stack gap={8}>
        <Title order={2} className="text-primary-text-body prose-lg/semi-bold">
          Verify your Identity
        </Title>

        <Text className="prose-sm/regular text-primary-text-subtle">
          Choose your preferred method to verify your identity
        </Text>
      </Stack>

      <Tabs value={activity} onChange={setActivity} variant="pill">
        <Stack gap={20}>
          <Tabs.List>
            <Tabs.Tab hidden value="otp" leftSection={<Mail01Icon />} flex={1}>
              OTP Code
            </Tabs.Tab>
            <Tabs.Tab value="qr-code" leftSection={<QRIcon />} flex={1}>
              QR code
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="qr-code">
            <Form form={form} onSubmit={handleSubmit}>
              <Stack gap={20}>
                <Stack gap={10}>
                  <Text className="prose-base/medium text-primary-text-body">
                    Scan QR
                  </Text>

                  <Stack
                    w="100%"
                    c="gray.11"
                    className="rounded-lg bg-primary-background-dark"
                    align="center"
                    justify="center"
                    p={12}
                    gap={8}
                  >
                    <QRCode
                      value={otp_auth_url}
                      size={140}
                      fgColor="currentColor"
                    />
                  </Stack>
                </Stack>

                {form.values.showToken ? (
                  <Stack gap={10}>
                    <Text className="prose-base/medium text-primary-text-body">
                      Token
                    </Text>

                    <Text c="gray.11">{otp_base32}</Text>
                  </Stack>
                ) : null}

                <Stack gap={10}>
                  <Text className="prose-base/medium text-primary-text-body">
                    Verification code
                  </Text>

                  <PinInput
                    w="100%"
                    style={{
                      flexWrap: "nowrap",
                      justifyContent: "space-between",
                    }}
                    styles={{
                      input: {
                        fontSize: 32,
                      },
                    }}
                    inputMode="numeric"
                    aria-label="One time code"
                    type="number"
                    size="lg"
                    {...form.getInputProps("otp")}
                  />

                  <Text fz={14} className="text-primary-text-subtle">
                    Didn&apos;t get a code? Click to{" "}
                    <CopyButton value={otp_base32}>
                      {({ copied, copy }) => (
                        <UnstyledButton
                          onClick={copy}
                          className="underline mantine-active"
                          fz={14}
                        >
                          {copied ? "copied" : "copy"}
                        </UnstyledButton>
                      )}
                    </CopyButton>{" "}
                    the code, or{" "}
                    <UnstyledButton
                      onClick={handleShowToken}
                      className="underline mantine-active"
                      fz={14}
                    >
                      display
                    </UnstyledButton>{" "}
                    it
                  </Text>
                </Stack>

                <Flex gap={12} wrap="wrap">
                  <Button
                    type="button"
                    variant="default"
                    flex={1}
                    miw="fit-content"
                    onClick={() => {
                      modals.close(MODALS.TWO_FACTOR_AUTHENTICATION);
                    }}
                  >
                    Cancel
                  </Button>

                  <Button type="submit" flex={1} miw="fit-content">
                    Confirm
                  </Button>
                </Flex>
              </Stack>
            </Form>
          </Tabs.Panel>
        </Stack>
      </Tabs>
    </Stack>
  );
}
